import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { assets } from '../constants/theme';

const grades = [
  {
    id: 'arabica-a',
    badge: 'Specialty Grade',
    badgeClass: 'badge-gold',
    name: 'Arabica A Grade',
    tagline: 'Complex, Nuanced, Balanced',
    origin: 'Kenyan Highlands, Ethiopia, Colombia',
    altitude: '1,200 – 2,200m',
    score: '82+ SCA Score',
    harvest: 'Varies by Region',
    processing: 'Washed, Natural, Honey',
    varietals: 'Bourbon, Typica, Catuai',
    price: '$14 – $18 per bag',
    img: assets.featureLeft,
    heroDesc: 'Arabica A Grade represents the higher tier of our Arabica selection — larger beans with exceptional consistency and a flavor complexity that elevates any brewing method. Carefully cultivated at altitude and processed with precision, this coffee embodies the art and science of specialty coffee production.',
    tastingNotes: {
      primary: ['Chocolate', 'Caramel', 'Roasted Nuts'],
      secondary: ['Floral Notes', 'Citrus Hints'],
      finish: 'Clean, Smooth, Lingering Sweetness',
    },
    brewing: [
      { method: 'Pour Over', grind: 'Medium', temp: '200–205°F', time: '4 min' },
      { method: 'French Press', grind: 'Coarse', temp: '200°F', time: '4–5 min' },
      { method: 'Espresso', grind: 'Fine', temp: '200°F', time: '25–30 sec' },
    ],
    impact: 'Direct trade with 50+ farmer cooperatives across Kenya, Ethiopia, and Colombia. Our A Grade program pays 25–30% above market price.',
  },
  {
    id: 'arabica-b',
    badge: 'Daily Specialty',
    badgeClass: 'badge-cream',
    name: 'Arabica B Grade',
    tagline: 'Approachable, Reliable, Full-Bodied',
    origin: 'Kenyan Highlands, West Africa',
    altitude: '1,000 – 2,000m',
    score: '80+ SCA Score',
    harvest: 'Multiple Pickings per Season',
    processing: 'Washed, Natural, Pulped Natural',
    varietals: 'Mundo Novo, Catuai, Robusta Blends',
    price: '$11 – $15 per bag',
    img: assets.featureRight,
    heroDesc: 'Arabica B Grade is your reliable daily specialty companion — a coffee that delivers consistent quality, rich body, and approachable flavors without compromise. This grade represents excellent value in the specialty market, ideal for high-volume brewing without sacrificing the quality your customers deserve.',
    tastingNotes: {
      primary: ['Dark Chocolate', 'Brown Sugar', 'Spice'],
      secondary: ['Subtle Fruit Undertones', 'Earthy Notes'],
      finish: 'Smooth, Full, Slightly Earthy',
    },
    brewing: [
      { method: 'Drip / Filter', grind: 'Medium', temp: '195–205°F', time: '5–6 min' },
      { method: 'AeroPress', grind: 'Medium-Fine', temp: '200°F', time: '1.5–2 min' },
      { method: 'Moka Pot', grind: 'Fine-Medium', temp: '—', time: '4–5 min' },
    ],
    impact: 'Fair trade certified. Our B Grade partners provide stable income to farming families across Kenya\'s highland and coastal regions.',
  },
  {
    id: 'kenyan-aa',
    badge: 'Premium Flagship',
    badgeClass: 'badge-dark',
    name: 'Kenyan AA',
    tagline: 'Fruit-Forward, Wine-Like, Complex',
    origin: "Mt. Kenya Slopes, Central Kenya",
    altitude: '1,400 – 2,200m',
    score: '84–88 SCA Score',
    harvest: 'October – December (Main Crop)',
    processing: 'Traditional Double Washed',
    varietals: 'SL28, SL34, Batian, Ruiru 11',
    price: '$16 – $22 per bag',
    img: assets.imgBrew,
    heroDesc: 'Kenya AA — the crown jewel of African specialty coffee. "AA" designates the largest bean size in Kenya\'s prestigious grading system, and with it comes an unparalleled depth of flavor, lively acidity, and the complexity that has made Kenyan coffee legendary among specialty buyers worldwide. Every bag represents months of careful cultivation and centuries of agricultural tradition.',
    tastingNotes: {
      primary: ['Blackcurrant', 'Blueberry', 'Raspberry'],
      secondary: ['Jasmine', 'Citrus Zest', 'Dark Chocolate'],
      finish: 'Long, Juicy, Wine-Like Finish',
    },
    flavourWheel: ['Berry', 'Stone Fruit', 'Black Tea', 'Caramel', 'Floral', 'Citrus'],
    brewing: [
      { method: 'Pour Over / Chemex', grind: 'Medium', temp: '200–205°F', time: '4 min' },
      { method: 'AeroPress', grind: 'Medium', temp: '200°F', time: '2–3 min' },
      { method: 'Espresso', grind: 'Fine', temp: '200°F', time: '28–32 sec' },
    ],
    impact: 'Direct relationship with 50+ cooperatives. Premium prices paid (20–30% above market). Women empowerment programs and replanting initiatives active on Mt. Kenya slopes.',
    specialFacts: [
      { label: 'Altitude', val: 'Creates dense beans with concentrated flavors' },
      { label: 'Volcanic Soil', val: 'Rich minerals contribute to complexity' },
      { label: 'Climate', val: 'Temperature variations develop fruit-forward notes' },
      { label: 'Processing', val: 'Traditional methods preserve natural acidity' },
    ],
  },
];

export default function CoffeeTypesPage() {
  useEffect(() => {
    document.title = 'Coffee Education | Arabica A, B & Kenyan AA | Savana Sips';
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('fadeIn');
      });
    }, { threshold: 0.05 });

    document.querySelectorAll('.ctp-fade').forEach((el, i) => {
      el.style.transitionDelay = `${i * 0.1}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const s = {
    page: { backgroundColor: '#FAF7F2', paddingTop: '100px' },
    pageHero: {
      position: 'relative',
      padding: '6rem 6% 5rem',
      backgroundColor: '#1C1007',
      overflow: 'hidden',
      textAlign: 'center',
    },
    heroBg: {
      position: 'absolute', inset: 0,
      backgroundImage: `url('${assets.imgFarm}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center 40%',
      opacity: 0.15,
    },
    heroContent: { position: 'relative', zIndex: 1 },
    breadcrumb: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '0.5rem', marginBottom: '1.5rem',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.75rem', color: 'rgba(250,247,242,0.5)',
      letterSpacing: '0.05em',
    },
    pageTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(2.8rem, 6vw, 5rem)',
      color: '#FAF7F2', fontWeight: 400,
      lineHeight: 1.08, marginBottom: '1.25rem',
    },
    accentText: { fontStyle: 'italic', color: '#D96D29' },
    pageSubtitle: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '1.05rem', color: 'rgba(250,247,242,0.7)',
      lineHeight: 1.7, maxWidth: '580px', margin: '0 auto 2rem',
    },
    tabs: {
      display: 'flex', justifyContent: 'center',
      gap: '0', borderBottom: '1px solid rgba(250,247,242,0.1)',
      marginTop: '3rem',
    },
    tab: (active) => ({
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: active ? '#D96D29' : 'rgba(250,247,242,0.5)',
      background: 'none', border: 'none',
      padding: '1rem 2rem',
      borderBottom: active ? '2px solid #D96D29' : '2px solid transparent',
      cursor: 'pointer', transition: 'all 0.3s ease',
    }),
    gradesSection: { padding: '5rem 6%', maxWidth: '1240px', margin: '0 auto' },
    gradeCard: {
      backgroundColor: '#fff',
      border: '1px solid #EDE5DC',
      marginBottom: '5rem',
      overflow: 'hidden',
    },
    gradeImg: { width: '100%', height: '380px', objectFit: 'cover' },
    gradeBody: { padding: '3rem 3rem 3.5rem' },
    gradeBadge: { marginBottom: '1rem' },
    gradeName: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      color: '#1C1209', marginBottom: '0.5rem',
    },
    gradeTagline: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.85rem', fontWeight: 500,
      color: '#A0693A', letterSpacing: '0.08em',
      textTransform: 'uppercase', marginBottom: '1.5rem',
    },
    gradeDesc: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '1rem', lineHeight: 1.8,
      color: '#3A2010', marginBottom: '2.5rem',
      maxWidth: '680px',
    },
    infoGrid: {
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '1.25rem', marginBottom: '2.5rem',
      paddingBottom: '2.5rem',
      borderBottom: '1px solid #EDE5DC',
    },
    infoItem: {
      display: 'flex', flexDirection: 'column',
      gap: '0.3rem',
    },
    infoLabel: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.67rem', fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: '#B08060',
    },
    infoVal: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.92rem', fontWeight: 600, color: '#1C1209',
    },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', marginBottom: '2.5rem' },
    sectionTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '1.55rem', color: '#1C1209', marginBottom: '1.25rem',
    },
    notesGroup: { display: 'flex', flexDirection: 'column', gap: '1rem' },
    notesRow: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem' },
    notesLabel: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: '#8B5E3C', marginBottom: '0.4rem',
    },
    brewTable: {
      width: '100%', borderCollapse: 'collapse',
    },
    brewTh: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: '#B08060',
      textAlign: 'left', padding: '0.5rem 0.75rem',
      borderBottom: '1px solid #EDE5DC',
    },
    brewTd: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.88rem', color: '#3A2010',
      padding: '0.65rem 0.75rem',
      borderBottom: '1px solid #F3ECE4',
    },
    impactBox: {
      backgroundColor: '#FAF3EB',
      border: '1px solid #EDE5DC',
      padding: '1.5rem 2rem',
      marginBottom: '2rem',
    },
    impactText: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.92rem', color: '#4A2C1A', lineHeight: 1.75,
    },
    specialFactsGrid: {
      display: 'grid', gridTemplateColumns: '1fr 1fr',
      gap: '1rem', margin: '1.5rem 0',
    },
    factItem: {
      backgroundColor: '#FAF3EB',
      padding: '1rem 1.25rem',
    },
    factLabel: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: '#D96D29', marginBottom: '0.3rem',
    },
    factVal: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.85rem', color: '#3A2010',
    },
    ctaRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '2rem' },
  };

  return (
    <div style={s.page} className="page-enter">
      {/* Page Hero */}
      <div style={s.pageHero}>
        <div style={s.heroBg} aria-hidden="true" />
        <div style={s.heroContent}>
          <div style={s.breadcrumb}>
            <Link to="/" style={{ color: 'rgba(250,247,242,0.45)', textDecoration: 'none' }}>Home</Link>
            <span>›</span>
            <span>Coffee Types</span>
          </div>
          <h1 style={s.pageTitle}>
            Coffee <span style={s.accentText}>Education</span><br />Hub
          </h1>
          <p style={s.pageSubtitle}>
            Explore our specialty coffees — from bright Arabica grades to complex Kenyan AA.
            Each carries a unique story, origin, and flavor that transforms your morning ritual.
          </p>
        </div>
      </div>

      {/* Grades */}
      <div style={s.gradesSection}>
        {grades.map((g) => (
          <div key={g.id} id={g.id} style={s.gradeCard} className="ctp-fade preFade">
            <img src={g.img} alt={g.name} style={s.gradeImg} loading="lazy" />
            <div style={s.gradeBody}>
              <div style={s.gradeBadge}>
                <span className={`badge ${g.badgeClass}`}>{g.badge}</span>
              </div>
              <h2 style={s.gradeName}>{g.name}</h2>
              <div style={s.gradeTagline}>{g.tagline}</div>
              <p style={s.gradeDesc}>{g.heroDesc}</p>

              {/* Meta Info */}
              <div style={{ ...s.infoGrid, gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
                {[
                  { label: 'Origin', val: g.origin },
                  { label: 'Altitude', val: g.altitude },
                  { label: 'Score', val: g.score },
                  { label: 'Harvest', val: g.harvest },
                  { label: 'Processing', val: g.processing },
                  { label: 'Price', val: g.price },
                ].map(item => (
                  <div key={item.label} style={s.infoItem}>
                    <span style={s.infoLabel}>{item.label}</span>
                    <span style={s.infoVal}>{item.val}</span>
                  </div>
                ))}
              </div>

              {/* Tasting Notes + Brewing */}
              <div style={{ ...s.twoCol, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                <div>
                  <h3 style={s.sectionTitle}>Tasting Notes</h3>
                  <div style={s.notesGroup}>
                    <div>
                      <div style={s.notesLabel}>Primary</div>
                      <div style={s.notesRow}>
                        {g.tastingNotes.primary.map(n => <span key={n} className="tasting-tag">{n}</span>)}
                      </div>
                    </div>
                    <div>
                      <div style={s.notesLabel}>Secondary</div>
                      <div style={s.notesRow}>
                        {g.tastingNotes.secondary.map(n => <span key={n} className="tasting-tag">{n}</span>)}
                      </div>
                    </div>
                    <div>
                      <div style={s.notesLabel}>Finish</div>
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.88rem', color: '#4A2C1A', fontStyle: 'italic' }}>
                        {g.tastingNotes.finish}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 style={s.sectionTitle}>Brewing Guide</h3>
                  <table style={s.brewTable}>
                    <thead>
                      <tr>
                        {['Method', 'Grind', 'Temp', 'Time'].map(h => (
                          <th key={h} style={s.brewTh}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {g.brewing.map((b, i) => (
                        <tr key={i}>
                          <td style={s.brewTd}>{b.method}</td>
                          <td style={s.brewTd}>{b.grind}</td>
                          <td style={s.brewTd}>{b.temp}</td>
                          <td style={s.brewTd}>{b.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Special Facts (Kenya AA only) */}
              {g.specialFacts && (
                <div>
                  <h3 style={s.sectionTitle}>What Makes Kenya AA Special</h3>
                  <div style={s.specialFactsGrid}>
                    {g.specialFacts.map(f => (
                      <div key={f.label} style={s.factItem}>
                        <div style={s.factLabel}>{f.label}</div>
                        <div style={s.factVal}>{f.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Impact */}
              <div style={s.impactBox}>
                <div style={{ ...s.infoLabel, color: '#2D6A2E', marginBottom: '0.5rem' }}>
                  🌱 Sustainability & Impact
                </div>
                <p style={s.impactText}>{g.impact}</p>
              </div>

              <div style={s.ctaRow}>
                <Link to="/products" className="btn-primary">
                  Buy {g.name} →
                </Link>
                <Link to="/process" className="btn-secondary">
                  See Our Process
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
